import type { LoginResponse } from "@probetalab/contracts";
import { urlFor } from "./api.js";
import { publicLogoUrl, siteBranding } from "./site.js";

/**
 * The browser tab shows the laboratory, not the product, once someone is signed in (pilot request
 * 2026-11-02: "¿Puede salir también nuestro nombre y logo?"). Signed out — the login page, after
 * sign-out — it is Probeta Lab again. These defaults mirror `apps/web/index.html`.
 */
export const DEFAULT_TITLE = "Probeta Lab";
export const DEFAULT_ICON = "/icons/icon.svg";
const DEFAULT_ICON_TYPE = "image/svg+xml";

/** The lab's logo URL, keyed by tenant so an operator switching laboratories never gets a cached logo. */
export function labLogoUrl(tenantId: string): string {
  return urlFor("labLogo", { query: { t: tenantId } });
}

function iconLink(rel: "icon" | "apple-touch-icon"): HTMLLinkElement {
  let link = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement("link");
    link.rel = rel;
    document.head.appendChild(link);
  }
  return link;
}

/**
 * Who the login screen and the signed-out tab show, in order (ADR 0025): the laboratory the HOST
 * belongs to (its own host, told by the server); else the laboratory this DEVICE last signed in
 * to (the platform host, or offline); else nobody — Probeta Lab.
 */
export interface LoginIdentity {
  name: string;
  logo: string | null;
  source: "host" | "device";
}

export function loginIdentity(): LoginIdentity | null {
  const site = siteBranding();
  if (site?.lab) return { name: site.lab.name, logo: site.lab.has_logo ? publicLogoUrl() : null, source: "host" };
  const remembered = readRememberedLab();
  return remembered ? { name: remembered.name, logo: remembered.logo, source: "device" } : null;
}

/** Sets the tab title and the favicons for this session view, or the signed-out identity with `null`. */
export function applyLabBranding(me: Pick<LoginResponse, "tenant" | "lab_display_name" | "has_logo"> | null): void {
  const signedOut = me ? null : loginIdentity();
  document.title = me ? me.lab_display_name : signedOut ? signedOut.name : DEFAULT_TITLE;
  const href = me?.has_logo ? labLogoUrl(me.tenant.id) : (signedOut?.logo ?? DEFAULT_ICON);
  const icon = iconLink("icon");
  icon.href = href;
  // The lab's logo may be a PNG or a JPEG: let the browser read the type from the response.
  if (href === DEFAULT_ICON) icon.type = DEFAULT_ICON_TYPE;
  else icon.removeAttribute("type");
  iconLink("apple-touch-icon").href = href;
}

/**
 * The laboratory this device last signed in to (Very, 2026-11-02 21:47: "que el inicio de sesión
 * también lleve el logo"). Since ADR 0025 a laboratory's own host names it from the server; the
 * memory is the second tier — the platform host and offline — and it carries the laboratory's
 * preferred origin, so app.probetalab.com can send the lab's staff to their own address. Storage is
 * per origin, so a laboratory's host only ever remembers its own laboratory. Name, logo and address
 * are not personal data; nothing else is kept.
 */
export interface RememberedLab {
  name: string;
  logo: string | null;
  /** The laboratory's preferred origin, e.g. `https://sonder.probetalab.com`; null if unknown. */
  origin: string | null;
}

/** An origin we will link to: HTTPS, or a dev `http://*.localhost`; nothing with a path or credentials. */
export function safeOrigin(value: unknown): string | null {
  if (typeof value !== "string") return null;
  try {
    const u = new URL(value);
    const ok = u.protocol === "https:" || (u.protocol === "http:" && (u.hostname === "localhost" || u.hostname.endsWith(".localhost")));
    return ok && !u.username && !u.password ? u.origin : null;
  } catch {
    return null;
  }
}

const REMEMBERED_KEY = "probetalab.lab";
const MAX_LOGO_BYTES = 300_000;

export function readRememberedLab(): RememberedLab | null {
  try {
    const raw = localStorage.getItem(REMEMBERED_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<RememberedLab>;
    if (typeof v.name !== "string" || !v.name.trim()) return null;
    return { name: v.name, logo: typeof v.logo === "string" && v.logo.startsWith("data:image/") ? v.logo : null, origin: safeOrigin(v.origin) };
  } catch {
    return null;
  }
}

/** Keeps the signed-in lab's name and logo (as a data URL, the logo route needs a session) for the next login. */
export async function rememberLab(me: Pick<LoginResponse, "tenant" | "lab_display_name" | "has_logo" | "preferred_origin">): Promise<void> {
  let logo: string | null = null;
  if (me.has_logo) {
    try {
      const res = await fetch(labLogoUrl(me.tenant.id), { credentials: "include" });
      const blob = res.ok ? await res.blob() : null;
      if (blob && blob.type.startsWith("image/") && blob.size <= MAX_LOGO_BYTES) {
        logo = await new Promise<string | null>((resolve) => {
          const r = new FileReader();
          r.onload = () => resolve(typeof r.result === "string" ? r.result : null);
          r.onerror = () => resolve(null);
          r.readAsDataURL(blob);
        });
      }
    } catch {
      logo = readRememberedLab()?.logo ?? null;
    }
  }
  try {
    localStorage.setItem(REMEMBERED_KEY, JSON.stringify({ name: me.lab_display_name, logo, origin: safeOrigin(me.preferred_origin) } satisfies RememberedLab));
  } catch {
    // Storage full or disabled: the login screen simply shows Probeta Lab.
  }
}

/**
 * After the platform host refused a laboratory user (`platform-login-disabled`), remember where that
 * laboratory lives, keeping the logo already known, so this device points there from now on.
 */
export function rememberLabAddress(name: string, origin: string): void {
  const prev = readRememberedLab();
  const lab: RememberedLab = { name, logo: prev && prev.name === name ? prev.logo : null, origin: safeOrigin(origin) };
  try {
    localStorage.setItem(REMEMBERED_KEY, JSON.stringify(lab));
  } catch {
    // Storage full or disabled: nothing to keep.
  }
}
